/**
 * Memoised SVG shapes of annotation objects, drawn in page units (<PageSvg> viewBox). Each shape
 * re-renders only when its id / hlc (or fade / preview transform) changes; ink outlines are cached.
 */
import { memo } from 'react';
import type { AnnotationObject, AnnotationOf } from '../../shared/annotations';
import { SMUFL_BY_NAME } from '../../shared/smufl';
import { glyphOrigin, qToUnits, sizeUnits, TEXT_ASCENT, TEXT_LINE_HEIGHT, textLines } from './geometry';
import { cachedInkPath } from './ink';

export const TEXT_FONT = "system-ui, -apple-system, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif";
export const FADED_OPACITY = 0.22;

interface ShapeProps<K extends AnnotationObject['kind']> {
  obj: AnnotationOf<K>;
  H: number;
  faded: boolean;
  transform?: string;
}

function same<K extends AnnotationObject['kind']>(a: ShapeProps<K>, b: ShapeProps<K>): boolean {
  return a.obj.id === b.obj.id && a.obj.hlcMs === b.obj.hlcMs && a.H === b.H && a.faded === b.faded && a.transform === b.transform;
}

export const HighlightShape = memo(function HighlightShape({ obj, H, faded, transform }: ShapeProps<'highlight'>) {
  const a = qToUnits(obj.data.a[0], obj.data.a[1], H);
  const b = qToUnits(obj.data.b[0], obj.data.b[1], H);
  return (
    <line
      x1={a.x}
      y1={a.y}
      x2={b.x}
      y2={b.y}
      stroke={obj.data.color}
      strokeWidth={sizeUnits(obj.data.width)}
      strokeLinecap="butt"
      opacity={faded ? FADED_OPACITY : undefined}
      transform={transform}
    />
  );
}, same);

export const InkShape = memo(function InkShape({ obj, H, faded, transform }: ShapeProps<'ink'>) {
  const d = cachedInkPath(`${obj.id}:${obj.hlcMs}:${H}`, obj.data.pts, obj.data.width, H);
  return <path d={d} fill={obj.data.color} opacity={faded ? FADED_OPACITY : undefined} transform={transform} />;
}, same);

export const TextShape = memo(function TextShape({ obj, H, faded, transform }: ShapeProps<'text'>) {
  const at = qToUnits(obj.data.at[0], obj.data.at[1], H);
  const fs = sizeUnits(obj.data.size);
  const lines = textLines(obj.data.text);
  return (
    <text
      fill={obj.data.color}
      fontSize={fs}
      fontWeight={obj.data.bold ? 700 : 500}
      opacity={faded ? FADED_OPACITY : undefined}
      transform={transform}
      style={{ fontFamily: TEXT_FONT, whiteSpace: 'pre' }}
    >
      {lines.map((line, i) => (
        <tspan key={i} x={at.x} y={at.y + fs * TEXT_ASCENT + i * fs * TEXT_LINE_HEIGHT}>
          {line.length > 0 ? line : ' '}
        </tspan>
      ))}
    </text>
  );
}, same);

export const StampShape = memo(function StampShape({ obj, H, faded, transform }: ShapeProps<'stamp'>) {
  const g = SMUFL_BY_NAME.get(obj.data.glyph);
  if (!g) return null;
  const c = qToUnits(obj.data.at[0], obj.data.at[1], H);
  const em = sizeUnits(obj.data.size);
  const o = glyphOrigin(g, em, c.x, c.y);
  const rot = obj.data.rot ? `rotate(${obj.data.rot} ${c.x} ${c.y})` : undefined;
  const t = [transform, rot].filter(Boolean).join(' ') || undefined;
  return (
    <text x={o.x} y={o.y} fontSize={em} fill={obj.data.color} className="smufl" opacity={faded ? FADED_OPACITY : undefined} transform={t}>
      {g.text}
    </text>
  );
}, same);

/** Draws any non-highlight, non-connector object. */
export function ObjectShape({ obj, H, faded, transform }: { obj: AnnotationObject; H: number; faded: boolean; transform?: string }) {
  switch (obj.kind) {
    case 'ink':
      return <InkShape obj={obj} H={H} faded={faded} transform={transform} />;
    case 'text':
      return <TextShape obj={obj} H={H} faded={faded} transform={transform} />;
    case 'stamp':
      return <StampShape obj={obj} H={H} faded={faded} transform={transform} />;
    default:
      return null;
  }
}
